import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import type { InputNode, StepsTooltip } from '../types';

/**
 * steps tooltip 工具层：判断 + 富文本渲染。
 *
 * 富文本渲染（renderRichText）与 V1 core/tooltipController.tsx 1:1 一致——
 * 这套逻辑是纯 react-markdown 配置，与编辑器架构无关，直接复用：
 *   - **xxx**        → 紫色加粗（CSS .rte-steps-tooltip strong）
 *   - [文字](url)    → 紫色渐变胶囊按钮（.rte-steps-tooltip-link，新窗口打开）
 *   - :color[文本]{hex=#7C3AED bold=true} → 自定义颜色 directive
 */

/** tooltip 是 StepsTooltip 对象（已是对象形态，非 JSON 字符串）。 */
export function isStepsTooltip(
  tooltip: InputNode['tooltip'],
): tooltip is StepsTooltip {
  return (
    !!tooltip &&
    typeof tooltip === 'object' &&
    tooltip.type === 'steps' &&
    Array.isArray(tooltip.platforms) &&
    tooltip.platforms.length > 0
  );
}

/** tooltip 是 steps（兼容 string JSON 与对象两种来源）。返回解析后的对象或 null。 */
export function parseStepsTooltip(
  tooltip: InputNode['tooltip'],
): StepsTooltip | null {
  if (isStepsTooltip(tooltip)) return tooltip;
  if (typeof tooltip === 'string') {
    const s = tooltip.trim();
    if (!s.startsWith('{') || !s.endsWith('}')) return null;
    try {
      const obj = JSON.parse(s);
      return isStepsTooltip(obj) ? obj : null;
    } catch {
      return null;
    }
  }
  return null;
}

// sanitize 白名单：允许极少量内联样式（span style / font color），仅用于配置端可控的 tooltip 文案。
const tooltipMarkdownSchema = {
  ...defaultSchema,
  attributes: {
    ...(defaultSchema.attributes || {}),
    span: [
      ...((((defaultSchema.attributes || {}) as Record<string, unknown>).span as
        | string[]
        | undefined) || []),
      'style',
    ],
    font: [
      ...((((defaultSchema.attributes || {}) as Record<string, unknown>).font as
        | string[]
        | undefined) || []),
      'color',
    ],
  },
} as const;

/**
 * 自定义 directive：:color[文本]{hex=#7C3AED bold=true}
 * 中括号内容仍是 Markdown（可嵌 **加粗**）。
 */
const remarkColorDirective = () => {
  return (tree: unknown) => {
    type VisitTree = Parameters<typeof visit>[0];
    type VisitNode = Parameters<Parameters<typeof visit>[1]>[0];
    type DirectiveNode = {
      type: 'textDirective' | 'leafDirective' | 'containerDirective' | string;
      name?: string;
      attributes?: Record<string, unknown>;
      data?: {
        [key: string]: unknown;
        hName?: string;
        hProperties?: Record<string, unknown>;
      };
    };
    visit(tree as VisitTree, (node: VisitNode) => {
      const n = node as unknown as DirectiveNode;
      if (
        !n ||
        (n.type !== 'textDirective' &&
          n.type !== 'leafDirective' &&
          n.type !== 'containerDirective')
      ) {
        return;
      }
      if (n.name !== 'color') return;

      const attrs: Record<string, unknown> = n.attributes || {};
      const hexRaw = (attrs.hex ?? attrs.color ?? attrs.value) as unknown;
      const hex = typeof hexRaw === 'string' ? hexRaw.trim() : '';
      const color = /^#([0-9a-fA-F]{6})$/.test(hex) ? hex : '#7C3AED';

      const boldRaw = attrs.bold as unknown;
      const bold =
        boldRaw === true || boldRaw === 'true' || boldRaw === 1 || boldRaw === '1';

      const style = `${bold ? 'font-weight:700;' : ''}color:${color};`;
      n.data = n.data || {};
      n.data.hName = 'span';
      n.data.hProperties = { style };
    });
  };
};

// CJK 兼容：CommonMark 的 emphasis 开口须 left-flanking，`演示**"观数"**` 这类
// `**` 紧贴中文+标点时不构成 delimiter run，会被当字面字符。交给 ReactMarkdown 前
// 先把 `**xxx**` 预处理成 <strong>xxx</strong>，由 rehypeRaw + sanitize 接管。
const preprocessRichText = (raw: string): string =>
  raw.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');

// components 必须是模块级稳定引用：每次渲染新建会让 React 卸载/重挂 <a>，
// 导致 mousedown/mouseup 命中不同元素 → 浏览器不派发 click。
const MarkdownParagraph: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => <p style={{ margin: 0 }}>{children}</p>;

const MarkdownLink: React.FC<{ href?: string; children?: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    className="rte-steps-tooltip-link"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onMouseDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
  >
    <span className="rte-steps-tooltip-link-text">{children}</span>
    <i
      className="iconfont icon-tiaozhuan2 rte-steps-tooltip-link-icon"
      aria-hidden="true"
    />
  </a>
);

const markdownComponents = {
  p: MarkdownParagraph,
  a: MarkdownLink,
} as const;

const markdownRemarkPlugins = [remarkDirective, remarkColorDirective] as const;
const markdownRehypePlugins = [
  rehypeRaw,
  [rehypeSanitize, tooltipMarkdownSchema],
] as const;

/** 渲染 steps tooltip 的富文本（description / stepDescriptions / disclaimer 通用）。 */
export const renderRichText = (text: string): React.ReactNode => (
  <ReactMarkdown
    remarkPlugins={markdownRemarkPlugins as never}
    rehypePlugins={markdownRehypePlugins as never}
    components={markdownComponents}
  >
    {preprocessRichText(text)}
  </ReactMarkdown>
);
